import type { ColumnDef, OnChangeFn, PaginationState, SortingState } from "@tanstack/react-table";
import { Loader2, Play, Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "@/components/admin/DataTable";
import { SearchInput } from "@/components/admin/SearchInput";
import { type BadgeTone, StatusBadge } from "@/components/admin/StatusBadge";
import { useAssessmentFixtures, useRunAssessmentFixture } from "@/hooks/admin/assessmentFixtures";
import { AdminLayout } from "@/layouts/AdminLayout";
import type { AssessmentFixture, AssessmentRunStatus } from "@/types";

const PAGE_SIZE = 25;

// Backend sorts by these columns; map TanStack column ids onto those.
const SORTABLE = new Set(["name", "topic_title", "created_at", "updated_at"]);

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function statusTone(status: AssessmentRunStatus): BadgeTone {
  switch (status) {
    case "completed":
      return "success";
    case "failed":
      return "danger";
    case "pending":
      return "warning";
    default:
      return "neutral";
  }
}

export default function AdminAssessmentFixturesPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState<SortingState>([{ id: "updated_at", desc: true }]);
  const [runError, setRunError] = useState<string | null>(null);

  const sort = sorting[0];
  const fixturesQuery = useAssessmentFixtures({
    page,
    page_size: PAGE_SIZE,
    sort_by: (sort?.id ?? "updated_at") as "name" | "topic_title" | "created_at" | "updated_at",
    sort_order: sort ? (sort.desc ? "desc" : "asc") : "desc",
    q: search || undefined,
  });
  const data = fixturesQuery.data ?? null;
  const error =
    runError ??
    (fixturesQuery.isError
      ? fixturesQuery.error instanceof Error
        ? fixturesQuery.error.message
        : "Failed to load fixtures"
      : null);

  const runFixture = useRunAssessmentFixture();
  const runningId = runFixture.isPending ? runFixture.variables?.id : null;

  const handleRun = useCallback(
    async (id: string) => {
      setRunError(null);
      try {
        const { run_id } = await runFixture.mutateAsync({ id });
        navigate(`/admin/assessment-fixtures/runs/${run_id}`);
      } catch (e) {
        setRunError(e instanceof Error ? e.message : "Failed to enqueue run");
      }
    },
    [navigate, runFixture]
  );

  const columns = useMemo<ColumnDef<AssessmentFixture>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => (
          <button
            type="button"
            onClick={() => navigate(`/admin/assessment-fixtures/${row.original.id}`)}
            className="font-medium text-gray-900 hover:underline"
          >
            {row.original.name}
          </button>
        ),
      },
      {
        id: "last_run",
        header: "Last Run",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-sm text-gray-600">
            {formatDate(row.original.last_run?.ran_at ?? null)}
          </span>
        ),
      },
      {
        id: "last_score",
        header: "Last Score",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-sm text-gray-600">{row.original.last_run?.final_score ?? "—"}</span>
        ),
      },
      {
        id: "status",
        header: "Status",
        enableSorting: false,
        cell: ({ row }) =>
          row.original.last_run ? (
            <StatusBadge tone={statusTone(row.original.last_run.status)}>
              {row.original.last_run.status}
            </StatusBadge>
          ) : (
            <span className="text-sm text-gray-400">never run</span>
          ),
      },
      {
        id: "run_count",
        header: "Runs",
        enableSorting: false,
        cell: ({ row }) => <span className="text-sm text-gray-600">{row.original.run_count}</span>,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <div className="text-right">
            <button
              type="button"
              disabled={runningId === row.original.id}
              onClick={() => handleRun(row.original.id)}
              className="inline-flex items-center gap-1 rounded bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
            >
              {runningId === row.original.id ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Play className="h-3 w-3" />
              )}
              Run
            </button>
          </div>
        ),
      },
    ],
    [navigate, runningId, handleRun]
  );

  const pagination: PaginationState = { pageIndex: page - 1, pageSize: PAGE_SIZE };
  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const next = typeof updater === "function" ? updater(pagination) : updater;
    setPage(next.pageIndex + 1);
  };
  const onSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      const valid = next.filter((s) => SORTABLE.has(s.id));
      setPage(1);
      return valid.length ? valid : prev;
    });
  };

  return (
    <AdminLayout>
      <div className="flex h-full flex-col gap-4">
        <DataTable
          className="h-full"
          columns={columns}
          data={data?.items ?? []}
          isLoading={fixturesQuery.isPending}
          error={error}
          onRetry={() => fixturesQuery.refetch()}
          emptyMessage="No fixtures yet. Click New Fixture to create one."
          getRowId={(f) => f.id}
          toolbar={
            <div className="flex items-center justify-between gap-3">
              <SearchInput
                value={search}
                onChange={(v) => {
                  setSearch(v);
                  setPage(1);
                }}
                placeholder="Search fixtures…"
              />
              <button
                type="button"
                onClick={() => navigate("/admin/assessment-fixtures/new")}
                className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600"
              >
                <Plus className="h-4 w-4" /> New Fixture
              </button>
            </div>
          }
          manualPagination
          pageCount={data?.total_pages ?? 0}
          rowCount={data?.total ?? 0}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          manualSorting
          sorting={sorting}
          onSortingChange={onSortingChange}
        />
      </div>
    </AdminLayout>
  );
}
